import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Overlay } from './Overlay';
import { FormContext } from './formContext';
import { FormField } from './FormField';
import { Input } from './controls';
import type { WithClassName } from './types';

export type ConfirmDialogProps = WithClassName & {
  open: boolean; title: string; description: ReactNode;
  level: 'info' | 'warning' | 'destructive'; confirmLabel: string;
  confirmText?: string; impact?: ReactNode; busy: boolean; error?: string;
  onConfirm: () => Promise<void>; onCancel: () => void;
  /** Assertive semantics are opt-in and limited to short confirmations. */
  urgent?: boolean; cancelLabel?: string;
};

export function ConfirmDialog({ open, ...props }: ConfirmDialogProps) {
  // Mount a fresh attempt whenever the controlled dialog is reopened.
  return open ? <FormContext.Provider value={null}><OpenConfirmDialog {...props} /></FormContext.Provider> : null;
}

function OpenConfirmDialog({ title, description, level, confirmLabel, confirmText, impact, busy, error,
  onConfirm, onCancel, urgent = false, cancelLabel = 'Cancel', className }: Omit<ConfirmDialogProps, 'open'>) {
  const id = useId();
  const cancel = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const [typed, setTyped] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string>();
  const working = busy || submitting;
  const hasImpact = impact !== undefined && impact !== null;
  const displayedError = failure || error;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { if (displayedError) errorRef.current?.focus(); }, [displayedError]);

  async function confirm() {
    if (working || inFlight.current) return;
    if (confirmText !== undefined && typed !== confirmText) { setMismatch(true); input.current?.focus(); return; }
    inFlight.current = true;
    setSubmitting(true);
    setFailure(undefined);
    try { await onConfirm(); }
    catch { if (mounted.current) setFailure('Unable to complete this action. Please try again.'); }
    finally {
      inFlight.current = false;
      if (mounted.current) setSubmitting(false);
    }
  }

  return <Overlay open kind="modal" size={hasImpact ? 'lg' : 'sm'} title={title}
    role={urgent && !hasImpact ? 'alertdialog' : 'dialog'} descriptionId={`${id}-description`}
    busy={working} onClose={onCancel} showClose={false}
    initialFocusRef={confirmText !== undefined ? input : cancel}
    className={`ui-confirm ui-confirm--${level} ${className ?? ''}`}>
    <div id={`${id}-description`} className="ui-confirm__description">{description}</div>
    {hasImpact && <div className="ui-confirm__impact">{impact}</div>}
    <form noValidate aria-busy={working || undefined} onChange={(event) => event.stopPropagation()} onBlur={(event) => event.stopPropagation()}
      onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); void confirm(); }}>
      {confirmText !== undefined && <FormField name="confirmation" label={`Type ${confirmText} to confirm`} required
        errors={mismatch ? [`Type ${confirmText} exactly to continue.`] : undefined}>
        {(control) => <Input {...control} ref={input} value={typed} disabled={working}
          onChange={(event) => { setTyped(event.target.value); if (mismatch && event.target.value === confirmText) setMismatch(false); }} />}
      </FormField>}
      {displayedError && <div ref={errorRef} className="ui-form-error" role="alert" tabIndex={-1}>{displayedError}</div>}
      <div className="ui-form-actions">
        <button ref={cancel} type="button" className="ui-button ui-button--ghost" disabled={working} onClick={onCancel}>{cancelLabel}</button>
        <button type="submit" disabled={working} className={`ui-button ${level === 'destructive' ? 'ui-button--danger' : 'ui-button--primary'}`}>
          {working ? 'Working…' : confirmLabel}
        </button>
      </div>
    </form>
  </Overlay>;
}
